import { useState, Fragment } from "react";
import PropTypes from "prop-types";
import {
  formatTime,
  formatDateTime,
  formatDuration,
  getCabinClassName,
  isOvernightFlight,
  getDayDifference,
  getCityName,
} from "../../utils/flightDataParser";
import { getAirlineLogo } from "../../utils/airlineImages";

// Calculate layover time in minutes between two segments
function calculateLayoverTime(currentSegment, nextSegment) {
  if (!currentSegment || !nextSegment) return 0;

  const arrivalTime = new Date(currentSegment.Destination.ArrTime);
  const departureTime = new Date(nextSegment.Origin.DepTime);

  // Calculate difference in milliseconds, then convert to minutes
  const diffInMs = departureTime - arrivalTime;
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

  return diffInMinutes > 0 ? diffInMinutes : 0;
}

// Layover Section Component
function LayoverSection({ segment, nextSegment }) {
  if (!segment || !nextSegment) return null;

  const layoverMinutes = calculateLayoverTime(segment, nextSegment);
  const city = segment.Destination.Airport.CityName;
  const airport = segment.Destination.Airport.AirportName;
  const arrivalTime = segment.Destination.ArrTime;
  const departureTime = nextSegment.Origin.DepTime;

  if (layoverMinutes <= 0) return null;

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4">
      <div className="flex items-start gap-3">
        <svg
          className="w-4 h-4 md:w-5 md:h-5 text-blue-600 flex-shrink-0 mt-0.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <div className="flex-1 min-w-0">
          <div className="text-xs md:text-sm font-medium text-blue-900">
            Layover: {formatDuration(layoverMinutes)} in {city}
          </div>
          <div className="text-xs text-blue-700 mt-1">
            {airport}
          </div>
          <div className="text-xs text-blue-600 mt-1">
            Arrives: {formatTime(arrivalTime)} • Departs: {formatTime(departureTime)}
          </div>
        </div>
      </div>
    </div>
  );
}

// Reusable Flight Card Component
function FlightSegmentCard({
  segment,
  segmentNumber,
}) {
  const origin = {
    city: getCityName(segment.Origin.Airport),
    airport: segment.Origin.Airport.AirportName,
    code: segment.Origin.Airport.AirportCode,
    terminal: segment.Origin.Airport.Terminal,
    time: segment.Origin.DepTime,
  };

  const destination = {
    city: getCityName(segment.Destination.Airport),
    airport: segment.Destination.Airport.AirportName,
    code: segment.Destination.Airport.AirportCode,
    terminal: segment.Destination.Airport.Terminal,
    time: segment.Destination.ArrTime,
  };

  const duration = segment.Duration;
  const airline = {
    name: segment.Airline.AirlineName,
    code: segment.Airline.AirlineCode,
    flightNumber: segment.Airline.FlightNumber,
  };

  const baggage = {
    checkin: segment.Baggage,
    cabin: segment.CabinBaggage,
  };

  const cabinClass = getCabinClassName(segment.CabinClass);
  const fareClass = segment.SupplierFareClass || "Standard";

  // Check if it's an overnight flight
  const isOvernight = isOvernightFlight(origin.time, destination.time);
  const dayDiff = isOvernight
    ? getDayDifference(origin.time, destination.time) || 1
    : 0;

  return (
    <div className="bg-white rounded-lg shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-100">
        <div className="flex items-center gap-2">
          {/* <svg
            className="w-5 h-5 md:w-6 md:h-6 text-orange-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
            />
          </svg> */}
          <h3 className="text-base md:text-lg font-semibold text-gray-900">
            Segment {segmentNumber}
          </h3>
        </div>
        <div className="text-xs md:text-sm font-medium text-gray-600">
          {airline.code}-{airline.flightNumber}
        </div>
      </div>

      {/* Flight Information */}
      <div className="p-4 md:p-6">
        {/* Fare Classification */}
        <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-4">
          <div className="bg-orange-500 text-white px-3 md:px-4 py-1 md:py-1.5 rounded text-xs md:text-sm font-medium">
            {cabinClass} | {fareClass}
          </div>
          {/* <div className="text-xs md:text-sm text-orange-500 font-medium">
            0 stops
          </div> */}
        </div>

        {/* Flight Route - Mobile & Tablet (Stacked Layout) */}
        <div className="block lg:hidden">
          {/* Origin */}
          <div className="mb-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-sm md:text-base font-semibold text-gray-900">
                  {origin.city}
                </div>
                <div className="text-2xl md:text-3xl font-bold text-gray-900 mt-1">
                  {formatTime(origin.time)}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {formatDateTime(origin.time)}
                </div>
              </div>
            </div>
            <div className="text-xs text-gray-700 font-medium mt-2">
              {origin.airport}
              {origin.terminal && `, Terminal ${origin.terminal}`}
            </div>
          </div>

          {/* Duration Indicator */}
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 relative">
              <div className="h-px bg-gray-300"></div>
              <div className="absolute right-0 top-1/2 -translate-y-1/2">
                <svg
                  className="w-3 h-3 text-gray-400"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M9 5l7 7-7 7V5z" />
                </svg>
              </div>
            </div>
            <div className="text-xs md:text-sm font-medium text-orange-500 whitespace-nowrap">
              {formatDuration(duration)}
            </div>
          </div>

          {/* Destination */}
          <div className="mb-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-sm md:text-base font-semibold text-gray-900">
                  {destination.city}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <div className="text-2xl md:text-3xl font-bold text-gray-900">
                    {formatTime(destination.time)}
                  </div>
                  {/* {isOvernight && (
                    <span className="text-sm md:text-base font-semibold text-orange-500">
                      +{dayDiff}
                    </span>
                  )} */}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {formatDateTime(destination.time)}
                </div>
              </div>
            </div>
            <div className="text-xs text-gray-700 font-medium mt-2">
              {destination.airport}
              {destination.terminal && `, Terminal ${destination.terminal}`}
            </div>
          </div>

          {/* Airline Info - Mobile */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <img
                  src={getAirlineLogo(airline.code, airline.name)}
                  alt={airline.name}
                  className="w-10 h-10 md:w-12 md:h-12 object-contain rounded-md"
                  onError={(e) => { e.target.src = "/nologo.gif"; }}
                />
              </div>
              <div>
                <div className="text-sm md:text-base font-bold text-gray-900">
                  {airline.name}
                </div>
                <div className="text-xs text-gray-500">
                  {airline.code}-{airline.flightNumber}
                </div>
              </div>
            </div>
          </div>

          {/* Baggage Info - Mobile */}
          <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <svg
                className="w-4 h-4 md:w-5 md:h-5 text-orange-500 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <div>
                <div className="text-xs text-gray-500">Cabin</div>
                <div className="text-xs md:text-sm font-medium text-gray-900">
                  {baggage.cabin}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <svg
                className="w-4 h-4 md:w-5 md:h-5 text-orange-500 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <rect
                  x="3"
                  y="6"
                  width="18"
                  height="14"
                  rx="2"
                  strokeWidth="2"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8 6V4c0-1.1.9-2 2-2h4c1.1 0 2 .9 2 2v2"
                />
              </svg>
              <div>
                <div className="text-xs text-gray-500">Check-in</div>
                <div className="text-xs md:text-sm font-medium text-gray-900">
                  {baggage.checkin}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Flight Route - Desktop (Horizontal Layout) */}
        <div className="hidden lg:flex items-start gap-0">
          <div className="flex-1 flex items-center gap-3">
            {/* Origin */}
            <div className="flex-1">
              <div className="text-base font-semibold text-gray-900 mb-1">
                {origin.city}
              </div>
              <div className="text-3xl font-medium text-gray-900 mb-2">
                {formatTime(origin.time)}
              </div>
              <div className="text-xs text-gray-500 mb-1">
                {formatDateTime(origin.time)}
              </div>
              <div className="text-xs text-gray-700 font-medium">
                {origin.airport}
                {origin.terminal && `, Terminal ${origin.terminal}`}
              </div>
            </div>

            {/* Duration */}
            <div className="flex flex-col items-center justify-center px-6 min-w-[180px] lg:min-w-[240px] xl:min-w-[320px] -mt-8">
              <div className="text-sm font-medium text-orange-500 mb-2.5">
                {formatDuration(duration)}
              </div>
              <div className="relative w-full">
                <div className="h-px bg-gray-300"></div>
                <div className="absolute right-0 top-1/2 -translate-y-1/2">
                  <svg
                    className="w-5 h-5 text-gray-400"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M9 5l7 7-7 7V5z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Destination */}
            <div className="flex-1">
              <div className="text-base font-semibold text-gray-900 mb-1">
                {destination.city}
              </div>
              <div className="flex items-center gap-2 mb-2">
                <div className="text-3xl font-medium text-gray-900">
                  {formatTime(destination.time)}
                </div>
                {/* {isOvernight && (
                  <span className="text-base font-semibold text-orange-500">
                    +{dayDiff}
                  </span>
                )} */}
              </div>
              <div className="text-xs text-gray-500 mb-1">
                {formatDateTime(destination.time)}
              </div>
              <div className="text-xs text-gray-700 font-medium">
                {destination.airport}
                {destination.terminal && `, Terminal ${destination.terminal}`}
              </div>
            </div>
          </div>

          {/* Right Section - Airline & Baggage */}
          <div className="flex items-start gap-8 border-l border-gray-200 pl-8">
            {/* Baggage Info */}
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-orange-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                <span className="text-sm font-medium text-gray-900">
                  Cabin: {baggage.cabin}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-orange-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <rect
                    x="3"
                    y="6"
                    width="18"
                    height="14"
                    rx="2"
                    strokeWidth="2"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8 6V4c0-1.1.9-2 2-2h4c1.1 0 2 .9 2 2v2"
                  />
                </svg>
                <span className="text-sm font-medium text-gray-900">
                  Check-in: {baggage.checkin}
                </span>
              </div>
            </div>

            {/* Airline Logo & Name */}
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <img
                  src={getAirlineLogo(airline.code, airline.name)}
                  alt={airline.name}
                  className="w-12 h-12 md:w-14 md:h-14 object-contain rounded-md"
                  onError={(e) => { e.target.src = "/nologo.gif"; }}
                />
              </div>
              <div>
                <div className="text-lg font-bold text-gray-900 leading-tight">
                  {airline.name}
                </div>
                <div className="text-sm text-gray-500">
                  {airline.code}-{airline.flightNumber}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Aircraft Info */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="text-xs text-gray-600">
            Aircraft: {segment.Craft || "N/A"}
          </div>
        </div>
      </div>
    </div>
  );
}

function BookingReview({ onContactSupport, fareQuoteData }) {

  // Extract data
  const results = fareQuoteData?.Results;

  // If no data, return loading state
  if (!results) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
        <div className="text-center text-gray-500">
          Loading flight details...
        </div>
      </div>
    );
  }

  // Extract segments (first journey - outbound)
  const segments = results.Segments?.[0] || [];
  const firstSegment = segments[0];
  const lastSegment = segments[segments.length - 1];

  if (!firstSegment || !lastSegment) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
        <div className="text-center text-gray-500">Invalid flight data</div>
      </div>
    );
  }

  const numStops = segments.length - 1;
  const classification = results.FareClassification;

  // For multi-segment flights, render each segment as a separate card
  if (numStops > 0) {
    return (
      <div className="space-y-4 md:space-y-6">
        {/* Main Header Card */}
        <div className="bg-[#FFF6EC] rounded-lg shadow-sm p-4 md:p-6">
          <div className="flex items-center justify-between min-h-[60px] py-2">
            <div className="flex items-center gap-2">
              <svg
                className="w-5 h-5 md:w-6 md:h-6 text-orange-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <rect
                  x="3"
                  y="4"
                  width="18"
                  height="18"
                  rx="2"
                  ry="2"
                  strokeWidth="2"
                />
                <line x1="16" y1="2" x2="16" y2="6" strokeWidth="2" />
                <line x1="8" y1="2" x2="8" y2="6" strokeWidth="2" />
                <line x1="3" y1="10" x2="21" y2="10" strokeWidth="2" />
              </svg>
              <h2 className="text-base md:text-xl font-semibold text-orange-500">
                Review Your Booking
              </h2>
            </div>
            <div className="flex items-center gap-2 md:gap-4">
            </div>
          </div>

          {/* Flight Summary */}
          <div className="mt-4">
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              <div className="text-sm md:text-lg font-bold text-gray-900 flex items-center gap-2">
                <span>{getCityName(firstSegment?.Origin?.Airport)}</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
                <span>{getCityName(lastSegment?.Destination?.Airport)}</span>
              </div>
              <span className="text-gray-400">•</span>
              <div className="text-xs md:text-sm text-gray-600 font-medium">
                {numStops} {numStops === 1 ? "stop" : "stops"}
              </div>
              <span className="text-gray-400">•</span>
              <div className="text-xs md:text-sm text-orange-500 font-medium">
                Total:{" "}
                {formatDuration(
                  lastSegment.AccumulatedDuration || lastSegment.Duration
                )}
              </div>
            </div>

            {/* Refundability Badge */}
            <div className="mt-4">
              {results.IsRefundable ? (
                <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 md:px-4 py-2">
                  <svg
                    className="w-4 h-4 md:w-5 md:h-5 text-green-700"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="text-xs md:text-sm text-green-700 font-medium">
                    Refundable Fare
                  </span>
                </div>
              ) : (
                <div className="inline-flex items-center gap-2 bg-yellow-50 border border-yellow-200 rounded-lg px-3 md:px-4 py-2">
                  <svg
                    className="w-4 h-4 md:w-5 md:h-5 text-yellow-700"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <span className="text-xs md:text-sm text-yellow-700 font-medium">
                    Non-refundable Fare
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Render each segment as a separate card */}
        {segments.map((segment, index) => (
          <Fragment key={index}>
            <FlightSegmentCard
              segment={segment}
              segmentNumber={index + 1}
            />
            {index < segments.length - 1 && (
              <LayoverSection
                segment={segment}
                nextSegment={segments[index + 1]}
              />
            )}
          </Fragment>
        ))}
      </div>
    );
  }

  // For single segment flights (non-stop), render the original single card
  const origin = {
    city: getCityName(firstSegment?.Origin?.Airport),
    airport: firstSegment.Origin.Airport.AirportName,
    code: firstSegment.Origin.Airport.AirportCode,
    terminal: firstSegment.Origin.Airport.Terminal,
    time: firstSegment.Origin.DepTime,
  };

  const destination = {
    city: getCityName(lastSegment?.Destination?.Airport),
    airport: lastSegment.Destination.Airport.AirportName,
    code: lastSegment.Destination.Airport.AirportCode,
    terminal: lastSegment.Destination.Airport.Terminal,
    time: lastSegment.Destination.ArrTime,
  };

  const totalDuration = lastSegment.AccumulatedDuration || lastSegment.Duration;

  const airline = {
    name: firstSegment.Airline.AirlineName,
    code: firstSegment.Airline.AirlineCode,
    flightNumber: firstSegment.Airline.FlightNumber,
  };

  const baggage = {
    checkin: firstSegment.Baggage,
    cabin: firstSegment.CabinBaggage,
  };

  const cabinClass = getCabinClassName(firstSegment.CabinClass);
  const fareClass = firstSegment.SupplierFareClass || "Standard";

  const isOvernight = isOvernightFlight(origin.time, destination.time);
  const dayDiff = isOvernight
    ? getDayDifference(origin.time, destination.time) || 1
    : 0;

  return (
    <div className="bg-white rounded-lg shadow-sm">
      {/* Header */}
      <div className="flex items-center bg-[#FFF6EC] justify-between min-h-[60px] p-4 md:px-6 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16 2V6M8 2V6" stroke="#FF4E01" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M21 13V12C21 8.22876 21 6.34315 19.8284 5.17157C18.6569 4 16.7712 4 13 4H11C7.22876 4 5.34315 4 4.17157 5.17157C3 6.34315 3 8.22876 3 12V14C3 17.7712 3 19.6569 4.17157 20.8284C5.34315 22 7.22876 22 11 22" stroke="#FF4E01" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M3 10H21" stroke="#FF4E01" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M13 19.5C13 19.5 14.3485 20.0067 15 22C15 22 18.1765 17 21 16" stroke="#FF4E01" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>

          <h2 className="text-base md:text-lg font-semibold text-orange-500">
            Review Your Booking
          </h2>
        </div>
        <div className="flex items-center gap-2 md:gap-4">
        </div>
      </div>

      {/* Flight Information */}
      <div className="p-4 md:p-6">
        {/* Fare Classification & Stops */}
        <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-4">
          <div
            className="text-white px-3 md:px-4 py-1 md:py-1.5  text-xs md:text-sm font-medium bg-[#FF670A] rounded-lg h-8"
          // style={{ backgroundColor: classification?.Color || "#f97316" }}
          >
            {cabinClass} | {fareClass}
          </div>
          <div className="text-xs md:text-sm text-orange-500 font-medium bg-[#FFF6EC] h-8 flex justify-center items-center pl-2 pr-2 rounded-lg">
            0 stops
          </div>
        </div>

        {/* Flight Route - Mobile & Tablet (Stacked Layout) */}
        <div className="block lg:hidden">
          {/* Origin */}
          <div className="mb-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-sm md:text-base font-semibold text-gray-900">
                  {origin.city}
                </div>
                <div className="text-2xl md:text-3xl font-medium text-gray-900 mt-1">
                  {formatTime(origin.time)}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {formatDateTime(origin.time)}
                </div>
              </div>
            </div>
            <div className="text-xs text-gray-700 font-medium mt-2">
              {origin.airport}
              {origin.terminal && `, Terminal ${origin.terminal}`}
            </div>
          </div>

          {/* Duration Indicator */}
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 relative">
              <div className="h-px bg-gray-300"></div>
              <div className="absolute right-0 top-1/2 -translate-y-1/2">
                <svg
                  className="w-3 h-3 text-gray-400"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M9 5l7 7-7 7V5z" />
                </svg>
              </div>
            </div>
            <div className="text-xs md:text-sm font-medium text-orange-500 whitespace-nowrap">
              {formatDuration(totalDuration)}
            </div>
          </div>

          {/* Destination */}
          <div className="mb-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-sm md:text-base font-semibold text-gray-900">
                  {destination.city}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <div className="text-2xl md:text-3xl font-medium text-gray-900">
                    {formatTime(destination.time)}
                  </div>
                  {/* {isOvernight && (
                    <span className="text-sm md:text-base font-semibold text-orange-500">
                      +{dayDiff}
                    </span>
                  )} */}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {formatDateTime(destination.time)}
                </div>
              </div>
            </div>
            <div className="text-xs text-gray-700 font-medium mt-2">
              {destination.airport}
              {destination.terminal && `, Terminal ${destination.terminal}`}
            </div>
          </div>

          {/* Airline Info - Mobile */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <img
                  src={getAirlineLogo(airline.code, airline.name)}
                  alt={airline.name}
                  className="w-10 h-10 md:w-12 md:h-12 object-contain rounded-md"
                  onError={(e) => { e.target.src = "/nologo.gif"; }}
                />
              </div>
              <div>
                <div className="text-sm md:text-base font-bold text-gray-900">
                  {airline.name}
                </div>
                <div className="text-xs text-gray-500">
                  {airline.code}-{airline.flightNumber}
                </div>
              </div>
            </div>
          </div>

          {/* Baggage Info - Mobile */}
          <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <svg
                className="w-4 h-4 md:w-5 md:h-5 text-orange-500 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <div>
                <div className="text-xs text-gray-500">Cabin</div>
                <div className="text-xs md:text-sm font-medium text-gray-900">
                  {baggage.cabin}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <svg
                className="w-4 h-4 md:w-5 md:h-5 text-orange-500 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <rect
                  x="3"
                  y="6"
                  width="18"
                  height="14"
                  rx="2"
                  strokeWidth="2"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8 6V4c0-1.1.9-2 2-2h4c1.1 0 2 .9 2 2v2"
                />
              </svg>
              <div>
                <div className="text-xs text-gray-500">Check-in</div>
                <div className="text-xs md:text-sm font-medium text-gray-900">
                  {baggage.checkin}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Flight Route - Desktop (Horizontal Layout) */}
        <div className="hidden lg:flex items-start gap-0">
          <div className="flex-1 flex items-center gap-3">
            {/* Origin */}
            <div className="flex-1  rounded-lg p-4">
              <div className="text-base font-semibold text-gray-900 mb-1">
                {origin.city}
              </div>
              <div className="text-3xl font-medium text-gray-900 mb-2">
                {formatTime(origin.time)}
              </div>
              <div className="text-xs text-gray-500 mb-1">
                {formatDateTime(origin.time)}
              </div>
              <div className="text-xs text-gray-700 font-medium">
                {origin.airport}
                {origin.terminal && `, Terminal ${origin.terminal}`}
              </div>
            </div>

            {/* Duration */}
            <div className="flex flex-col items-center justify-center px-6 min-w-[180px] lg:min-w-[240px] xl:min-w-[320px] -mt-8">
              <div className="bg-[#FFF6EC] h-5 flex justify-center items-center pl-2 pr-2 rounded-lg text-sm font-medium text-orange-500 mb-2.5">
                {formatDuration(totalDuration)}
              </div>
              <div className="relative w-full flex justify-center">
                <svg width="172" height="6" viewBox="0 0 172 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M-1.0252e-05 2.88672C-1.0252e-05 4.35948 1.1939 5.55339 2.66666 5.55339C4.13942 5.55339 5.33332 4.35948 5.33332 2.88672C5.33332 1.41396 4.13942 0.220052 2.66666 0.220052C1.1939 0.220052 -1.0252e-05 1.41396 -1.0252e-05 2.88672ZM171.167 2.88672L166.167 -3.26633e-05V5.77347L171.167 2.88672ZM2.66666 2.88672V3.38672H166.667V2.88672V2.38672H2.66666V2.88672Z" fill="black" />
                </svg>

              </div>
            </div>

            {/* Destination */}
            <div className="flex-1  rounded-lg p-4">
              <div className="text-base font-semibold text-gray-900 mb-1">
                {destination.city}
              </div>
              <div className="flex items-center gap-2 mb-2">
                <div className="text-3xl font-medium text-gray-900">
                  {formatTime(destination.time)}
                </div>
                {/* {isOvernight && (
                  <span className="text-base font-semibold text-orange-500">
                    +{dayDiff}
                  </span>
                )} */}
              </div>
              <div className="text-xs text-gray-500 mb-1">
                {formatDateTime(destination.time)}
              </div>
              <div className="text-xs text-gray-700 font-medium">
                {destination.airport}
                {destination.terminal && `, Terminal ${destination.terminal}`}
              </div>
            </div>
          </div>

          {/* Right Section - Meal, Baggage & Airline in single row */}
          <div className="flex items-center gap-20 border-l  pl-6 " style={{ height: '-webkit-fill-available' }}>
            <div className="flex flex-col gap-3"> {/* Paid Meal Icon */}
              <div className="flex items-center gap-2">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 14H22" stroke="#FF4E01" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M5 22L5.93056 21.0694C6.45933 20.5407 6.72371 20.2763 7.05684 20.126C7.38997 19.9756 7.76314 19.9523 8.50947 19.9057L11.7619 19.7024C13.3604 19.6025 14.1597 19.5525 14.863 19.2148C15.5664 18.877 16.1051 18.2844 17.1824 17.0993L20 14H16.5L14.7071 15.7929C14.5879 15.9121 14.5282 15.9718 14.4662 16.0244C14.1472 16.2954 13.7512 16.4594 13.3341 16.4934C13.2529 16.5 13.1686 16.5 13 16.5M13 16.5C13 16.0353 13 15.803 12.9616 15.6098C12.8038 14.8164 12.1836 14.1962 11.3902 14.0384C11.197 14 10.9647 14 10.5 14H9.71849C8.67345 14 8.15093 14 7.66095 14.126C7.29599 14.2198 6.94612 14.3647 6.62171 14.5564C6.18617 14.8138 5.81669 15.1833 5.07774 15.9223L2 19M13 16.5H9.5" stroke="#FF4E01" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M5 11.5C5 7.08171 8.58171 3.5 13 3.5M13 3.5C17.4183 3.5 21 7.08171 21 11.5M13 3.5V2" stroke="#FF4E01" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                </svg>

                <span className="text-sm font-medium text-gray-900">
                  Paid Meal
                </span>
              </div>

              {/* Baggage Weight Icon */}
              <div className="flex items-center gap-2">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.0018 14.0034V12.3621M12.0018 14.0034C10.3356 14.0034 8.66938 13.821 7.0032 13.4565M12.0018 14.0034C13.6684 14.0034 15.3351 13.8212 17.0018 13.4565M7.0032 13.4565C5.72831 13.1776 4.45343 12.7918 3.17854 12.2995C2.66898 12.1026 2.4142 12.0042 2.23938 11.7788C1.94932 11.4049 2.00174 10.8841 2.00174 10.4387C2.00174 9.20572 2.00174 7.58804 2.30254 7.26609C2.872 6.65661 3.95909 7.21761 4.59137 7.42272C9.53162 9.02529 14.4719 9.02529 19.4122 7.42272C20.0444 7.21761 21.1315 6.65661 21.701 7.26609C22.0018 7.58804 22.0018 9.20572 22.0018 10.4387C22.0018 11.0571 22.0018 11.3663 21.8658 11.6217C21.6568 12.0141 21.2154 12.1487 20.825 12.2995C19.5506 12.7916 18.2762 13.1776 17.0018 13.4565M7.0032 13.4565L7.00174 11.8149M17.0018 13.4565V11.8149" stroke="#FF4E01" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M19.002 19.0096C14.3353 20.3458 9.66862 20.3458 5.00195 19.0096" stroke="#FF4E01" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M19.191 2.99805C18.6141 4.30938 18.0839 6.55739 18.0839 7.58772M4.81544 2.99805C5.39237 4.30938 5.92253 6.55739 5.92253 7.58772M5.39913 15.5107C4.96029 16.8915 3.80051 19.6218 4.00426 21.0026M18.6142 15.5107C19.0531 16.8915 20.2129 19.6218 20.0091 21.0026" stroke="#FF4E01" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                </svg>

                <span className="text-sm font-medium text-gray-900">
                  {baggage.checkin}
                </span>
              </div></div>

            {/* Airline Logo & Name */}
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <img
                  src={getAirlineLogo(airline.code, airline.name)}
                  alt={airline.name}
                  className="w-12 h-12 md:w-14 md:h-14 object-contain rounded-md"
                  onError={(e) => { e.target.src = "/nologo.gif"; }}
                />
              </div>
              <div>
                <div className="text-lg font-bold text-gray-900">
                  {airline.name}
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Warning Banner */}
      <div className="mx-4 md:mx-6 mb-4 md:mb-6 bg-yellow-100 border-l-4 border-yellow-500 rounded-r-lg p-4">
        <div className="flex items-start gap-3">
          <svg
            className="w-5 h-5 text-yellow-700 flex-shrink-0 mt-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <div className="flex-1">
            <p className="text-sm font-semibold text-yellow-900">
              Important Note:
            </p>
            <p className="text-sm text-yellow-800 mt-1">
              The Fare Rules for your flight selection have changed. Please review them before proceeding.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

FlightSegmentCard.propTypes = {
  segment: PropTypes.object.isRequired,
  segmentNumber: PropTypes.number.isRequired,
};

LayoverSection.propTypes = {
  segment: PropTypes.object.isRequired,
  nextSegment: PropTypes.object.isRequired,
};

BookingReview.propTypes = {
  onContactSupport: PropTypes.func,
  fareQuoteData: PropTypes.object,
};

export default BookingReview;
